import { useState, useEffect } from "react";
import { api } from "@/App";
import { toast } from "sonner";
import {
  BarChart3,
  Users,
  FolderKanban,
  MessageSquare,
  FileText,
  MessageSquareQuote,
  ClipboardList,
  Send,
  TrendingUp,
  Activity,
  PieChart,
  RefreshCw,
  Calendar,
  Download,
  Filter,
  ChevronDown,
  ArrowUp,
  ArrowDown,
  Minus
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";

const StatCard = ({ icon: Icon, label, value, color, subtext, trend, trendValue }) => (
  <Card className="bg-slate-800/50 border-slate-700/50 hover:border-slate-600 transition-colors">
    <CardContent className="pt-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-indigo-400">{label}</p>
          <p className="text-3xl font-bold text-indigo-500">{value}</p>
          {subtext && <p className="text-xs text-slate-400 mt-1">{subtext}</p>}
          {trend && (
            <div className={`flex items-center gap-1 mt-1 text-xs ${
              trend === 'up' ? 'text-green-400' : trend === 'down' ? 'text-red-400' : 'text-indigo-400'
            }`}>
              {trend === 'up' ? <ArrowUp className="w-3 h-3" /> : 
               trend === 'down' ? <ArrowDown className="w-3 h-3" /> : 
               <Minus className="w-3 h-3" />}
              <span>{trendValue}</span>
            </div>
          )}
        </div>
        <div className={`p-3 rounded-xl ${color}`}>
          <Icon className="w-6 h-6" />
        </div>
      </div>
    </CardContent>
  </Card>
);

const StatusBar = ({ label, value, total, color }) => {
  const percentage = total > 0 ? (value / total) * 100 : 0;
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-sm">
        <span className="text-indigo-400 capitalize">{label}</span>
        <span className="text-slate-400">{value} ({percentage.toFixed(0)}%)</span>
      </div>
      <div className="h-2 bg-slate-400 rounded-full overflow-hidden">
        <div
          className={`h-full ${color} transition-all duration-500`}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
};

const DATE_RANGES = [
  { value: "7d", label: "Last 7 days" },
  { value: "30d", label: "Last 30 days" },
  { value: "90d", label: "Last 90 days" },
  { value: "year", label: "This year" },
  { value: "all", label: "All time" }
];

export default function Analytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState("30d");
  const [comparisonData, setComparisonData] = useState(null);

  useEffect(() => {
    fetchAnalytics();
  }, [dateRange]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/analytics/overview?range=${dateRange}`);
      setData(res.data);
      
      // Simulate comparison data (in real app, backend would provide this)
      setComparisonData({
        clients: { trend: 'up', value: '+2 this period' },
        projects: { trend: 'up', value: '+3 this period' },
        messages: { trend: 'up', value: '+5 this period' },
        proposals: { trend: 'same', value: 'No change' }
      });
    } catch (e) {
      console.error("Error fetching analytics:", e);
      toast.error("Failed to load analytics");
    } finally {
      setLoading(false);
    }
  };

  const exportAnalytics = () => {
    if (!data) return;
    
    const exportData = {
      exported_at: new Date().toISOString(),
      date_range: dateRange,
      usage: data.usage,
      counts: data.counts,
      project_statuses: data.project_statuses,
      message_tones: data.message_tones,
      recent_activity: data.recent_activity
    };
    
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `clientkit-analytics-${dateRange}-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    toast.success("Analytics exported!");
  };

  const exportCSV = () => {
    if (!data) return;
    
    const rows = [
      ['Metric', 'Value'],
      ['Clients', data.counts.clients],
      ['Projects', data.counts.projects],
      ['Messages', data.counts.messages],
      ['Proposals', data.counts.proposals],
      ['Testimonials', data.counts.testimonials],
      ['SOPs', data.counts.sops],
      ['Outreach', data.counts.outreach],
      ['', ''],
      ['Project Status', 'Count'],
      ['Active', data.project_statuses.active || 0],
      ['Pending', data.project_statuses.pending || 0],
      ['Paused', data.project_statuses.paused || 0],
      ['Completed', data.project_statuses.completed || 0],
      ['', ''],
      ['Message Tone', 'Count'],
      ['Friendly', data.message_tones.friendly || 0],
      ['Firm', data.message_tones.firm || 0],
      ['Formal', data.message_tones.formal || 0],
      ['', ''],
      ['Usage', 'Value'],
      ['Current', data.usage.current],
      ['Limit', data.usage.limit],
      ['Tier', data.usage.tier]
    ];
    
    const csv = rows.map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `clientkit-analytics-${dateRange}-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    toast.success("CSV exported!");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 text-indigo-400 animate-spin" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-12">
        <p className="text-indigo-400">Failed to load analytics</p>
        <button
          onClick={fetchAnalytics}
          className="mt-4 px-4 py-2 bg-indigo-500 hover:bg-indigo-600 text-white rounded-lg"
        >
          Retry
        </button>
      </div>
    );
  }

  const totalProjects = Object.values(data.project_statuses || {}).reduce((a, b) => a + b, 0);
  const totalMessages = Object.values(data.message_tones || {}).reduce((a, b) => a + b, 0);

  return (
    <div className="space-y-6" data-testid="analytics-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-indigo-500 flex items-center gap-2">
            <BarChart3 className="w-7 h-7 text-indigo-400" />
            Analytics
          </h1>
          <p className="text-indigo-400">Overview of your ClientKit activity</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/* Date Range Filter */}
          <Select value={dateRange} onValueChange={setDateRange}>
            <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
              <Calendar className="w-4 h-4 mr-2 text-indigo-400" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-slate-800 border-slate-700">
              {DATE_RANGES.map((range) => (
                <SelectItem key={range.value} value={range.value} className="text-indigo-500">
                  {range.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          {/* Export Dropdown */}
          <div className="relative group">
            <button className="flex items-center gap-2 px-3 py-2 bg-slate-400 hover:bg-slate-600 text-slate-400 rounded-lg transition-colors">
              <Download className="w-4 h-4" />
              Export
              <ChevronDown className="w-3 h-3" />
            </button>
            <div className="absolute right-0 mt-1 w-32 bg-slate-800 border border-slate-700 rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10">
              <button
                onClick={exportAnalytics}
                className="w-full px-3 py-2 text-left text-sm text-slate-400 hover:bg-slate-400 rounded-t-lg"
              >
                Export JSON
              </button>
              <button
                onClick={exportCSV}
                className="w-full px-3 py-2 text-left text-sm text-slate-400 hover:bg-slate-400 rounded-b-lg"
              >
                Export CSV
              </button>
            </div>
          </div>
          
          <button
            onClick={fetchAnalytics}
            className="flex items-center gap-2 px-3 py-2 bg-slate-400 hover:bg-slate-600 text-slate-400 rounded-lg transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            <span className="hidden sm:inline">Refresh</span>
          </button>
        </div>
      </div>

      {/* Active Filter Badge */}
      {dateRange !== "all" && (
        <div className="flex items-center gap-2">
          <Badge className="bg-indigo-500/20 text-indigo-400 border-indigo-500/30">
            <Filter className="w-3 h-3 mr-1" />
            {DATE_RANGES.find(r => r.value === dateRange)?.label}
          </Badge>
        </div>
      )}

      {/* Usage Stats */}
      <Card className="bg-gradient-to-r from-indigo-500/20 to-indigo-500/20 border-indigo-500/30">
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-4">
            <div>
              <p className="text-sm text-slate-400">Monthly Usage</p>
              <p className="text-2xl font-bold text-indigo-500">
                {data.usage.current} / {data.usage.limit} generations
              </p>
            </div>
            <div className="text-left sm:text-right">
              <p className="text-sm text-slate-400">Current Plan</p>
              <p className="text-lg font-semibold text-indigo-400 capitalize">{data.usage.tier}</p>
            </div>
          </div>
          <Progress value={data.usage.percentage} className="h-3" />
          <p className="text-xs text-indigo-400 mt-2">
            {data.usage.percentage}% of monthly limit used • {data.usage.limit - data.usage.current} remaining
          </p>
        </CardContent>
      </Card>

      {/* Main Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={Users}
          label="Clients"
          value={data.counts.clients}
          color="bg-blue-500/20 text-indigo-400"
          trend={comparisonData?.clients?.trend}
          trendValue={comparisonData?.clients?.value}
        />
        <StatCard
          icon={FolderKanban}
          label="Projects"
          value={data.counts.projects}
          color="bg-indigo-500/20 text-indigo-400"
          trend={comparisonData?.projects?.trend}
          trendValue={comparisonData?.projects?.value}
        />
        <StatCard
          icon={MessageSquare}
          label="Messages"
          value={data.counts.messages}
          color="bg-green-500/20 text-green-400"
          trend={comparisonData?.messages?.trend}
          trendValue={comparisonData?.messages?.value}
        />
        <StatCard
          icon={FileText}
          label="Proposals"
          value={data.counts.proposals}
          color="bg-orange-500/20 text-orange-400"
          trend={comparisonData?.proposals?.trend}
          trendValue={comparisonData?.proposals?.value}
        />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={MessageSquareQuote}
          label="Testimonials"
          value={data.counts.testimonials}
          color="bg-indigo-500/20 text-indigo-400"
        />
        <StatCard
          icon={ClipboardList}
          label="SOPs"
          value={data.counts.sops}
          color="bg-teal-500/20 text-teal-700"
        />
        <StatCard
          icon={Send}
          label="Outreach"
          value={data.counts.outreach}
          color="bg-indigo-500/20 text-indigo-400"
        />
        <StatCard
          icon={Activity}
          label="7-Day Activity"
          value={data.recent_activity.messages_7d + data.recent_activity.proposals_7d}
          color="bg-indigo-500/20 text-indigo-400"
          subtext={`${data.recent_activity.messages_7d} msgs, ${data.recent_activity.proposals_7d} proposals`}
        />
      </div>

      {/* Breakdown Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Project Status Breakdown */}
        <Card className="bg-slate-800/50 border-slate-700/50">
          <CardHeader>
            <CardTitle className="text-indigo-500 flex items-center gap-2">
              <PieChart className="w-5 h-5 text-indigo-400" />
              Project Status
            </CardTitle>
            <CardDescription className="text-indigo-400">
              Breakdown of {totalProjects} projects
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <StatusBar
              label="Active"
              value={data.project_statuses.active || 0}
              total={totalProjects}
              color="bg-green-500"
            />
            <StatusBar
              label="Pending"
              value={data.project_statuses.pending || 0}
              total={totalProjects}
              color="bg-indigo-500"
            />
            <StatusBar
              label="Paused"
              value={data.project_statuses.paused || 0}
              total={totalProjects}
              color="bg-orange-500"
            />
            <StatusBar
              label="Completed"
              value={data.project_statuses.completed || 0}
              total={totalProjects}
              color="bg-blue-500"
            />
            
            {/* Visual Pie Chart Representation */}
            <div className="flex justify-center pt-4">
              <div className="relative w-32 h-32">
                <svg viewBox="0 0 100 100" className="transform -rotate-90">
                  {(() => {
                    const statuses = [
                      { key: 'active', color: '#22c55e' },
                      { key: 'pending', color: '#eab308' },
                      { key: 'paused', color: '#f97316' },
                      { key: 'completed', color: '#3b82f6' }
                    ];
                    let offset = 0;
                    return statuses.map((status) => {
                      const value = data.project_statuses[status.key] || 0;
                      const pct = totalProjects > 0 ? (value / totalProjects) * 100 : 0;
                      const dashArray = `${pct} ${100 - pct}`;
                      const dashOffset = -offset;
                      offset += pct;
                      return (
                        <circle
                          key={status.key}
                          cx="50"
                          cy="50"
                          r="40"
                          fill="none"
                          stroke={status.color}
                          strokeWidth="20"
                          strokeDasharray={dashArray}
                          strokeDashoffset={dashOffset}
                        />
                      );
                    });
                  })()}
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-xl font-bold text-indigo-500">{totalProjects}</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Message Tone Breakdown */}
        <Card className="bg-slate-800/50 border-slate-700/50">
          <CardHeader>
            <CardTitle className="text-indigo-500 flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-green-400" />
              Message Tones
            </CardTitle>
            <CardDescription className="text-indigo-400">
              Breakdown of {totalMessages} generated replies
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <StatusBar
              label="Friendly"
              value={data.message_tones.friendly || 0}
              total={totalMessages}
              color="bg-green-500"
            />
            <StatusBar
              label="Firm"
              value={data.message_tones.firm || 0}
              total={totalMessages}
              color="bg-red-500"
            />
            <StatusBar
              label="Formal"
              value={data.message_tones.formal || 0}
              total={totalMessages}
              color="bg-blue-500"
            />
            
            {/* Visual Bar Chart */}
            <div className="flex items-end justify-center gap-4 pt-4 h-32">
              {[
                { key: 'friendly', color: 'bg-green-500', label: 'Friendly' },
                { key: 'firm', color: 'bg-red-500', label: 'Firm' },
                { key: 'formal', color: 'bg-blue-500', label: 'Formal' }
              ].map((tone) => {
                const value = data.message_tones[tone.key] || 0;
                const maxVal = Math.max(...Object.values(data.message_tones || {}), 1);
                const height = (value / maxVal) * 80;
                return (
                  <div key={tone.key} className="flex flex-col items-center gap-1">
                    <span className="text-xs text-indigo-400">{value}</span>
                    <div
                      className={`w-12 ${tone.color} rounded-t-lg transition-all duration-500`}
                      style={{ height: `${Math.max(height, 4)}px` }}
                    />
                    <span className="text-xs text-slate-400">{tone.label}</span>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Activity Summary */}
      <Card className="bg-slate-800/50 border-slate-700/50">
        <CardHeader>
          <CardTitle className="text-indigo-500 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-indigo-400" />
            Activity Summary
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 text-center">
            <div className="p-4 bg-slate-900/30 rounded-xl">
              <p className="text-2xl font-bold text-indigo-500">{data.recent_activity.messages_7d}</p>
              <p className="text-sm text-indigo-400">Messages this week</p>
            </div>
            <div className="p-4 bg-slate-900/30 rounded-xl">
              <p className="text-2xl font-bold text-indigo-500">{data.recent_activity.proposals_7d}</p>
              <p className="text-sm text-indigo-400">Proposals this week</p>
            </div>
            <div className="p-4 bg-slate-900/30 rounded-xl">
              <p className="text-2xl font-bold text-indigo-500">{data.counts.clients}</p>
              <p className="text-sm text-indigo-400">Total clients</p>
            </div>
            <div className="p-4 bg-slate-900/30 rounded-xl">
              <p className="text-2xl font-bold text-indigo-500">
                {data.project_statuses.active || 0}
              </p>
              <p className="text-sm text-indigo-400">Active projects</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
